import { type ShowRes } from '@on3/api';
import {
  On3IconApplePodcast,
  On3IconSpotify,
  On3IconYoutube,
} from '@on3/icons';
import { useSite } from '@on3/ui-lib';
import { useAuth } from '@on3/ui-lib/src/contexts/AuthProvider';
import { databaseUrl } from '@on3/ui-lib/utils/api';
import { BreadCrumb } from 'components/BreadCrumb/BreadCrumb';
import { ResizedImage } from 'components/Image/ResizedImage';
import { TextLink } from 'components/UI/Link/TextLink';
import { Text } from 'components/UI/Typography/Text';

import styles from './ShowsDetails.module.scss';

interface IShowsDetailsProps {
  title: string;
  showDetails: ShowRes;
}

export const ShowsDetails = ({ title, showDetails }: IShowsDetailsProps) => {
  const {
    description,
    asset,
    appleMeta,
    spotifyMeta,
    youTubeMeta,
    simplecastMeta,
    key,
  } = showDetails ?? {};
  const { currentSite } = useSite();
  const { hasShowsAdmin } = useAuth();

  const displayWatchContainer =
    appleMeta?.url || spotifyMeta?.url || youTubeMeta?.url;
  const showPath = `${currentSite?.isTeam ? currentSite?.url : ''}/shows/`;
  const teamName = (currentSite?.isTeam && currentSite?.name) || 'On3';
  const breadCrumbTitle = `Shows and Podcasts`;
  const podcastSource = simplecastMeta?.podcastId
    ? `https://player.simplecast.com/${simplecastMeta?.podcastId}?dark=true&show=true`
    : null;
  const siteColor =
    (currentSite?.linkColor || currentSite?.primaryColor) ?? '#fe3b1f';

  return (
    <>
      <BreadCrumb
        color={siteColor}
        crumbs={[
          { title: teamName, href: '/' },
          { title: breadCrumbTitle, href: showPath },
        ]}
        title={title}
      />
      <div className={styles.block}>
        <header>
          <h1>{title}</h1>
          {hasShowsAdmin() && (
            <TextLink href={`${databaseUrl}/shows/${key}/edit/`}>Edit</TextLink>
          )}
        </header>
        <section className={styles.podcast}>
          <h2 className="sr-only">About {title}</h2>
          <div className={styles.details}>
            <ResizedImage
              altText={title ?? 'Show Avatar'}
              className={styles.avatar}
              domain={asset?.domain}
              fit="cover"
              gravity="0.5x0.5"
              height={200}
              loading="eager"
              quality={100}
              source={asset?.source}
              width={200}
            />
            <div className={styles.detailsWrapper}>
              <Text className={styles.description}>{description}</Text>

              {displayWatchContainer && (
                <>
                  <h3>Watch / Listen On</h3>
                  <div className={styles.buttonWrapper}>
                    {appleMeta?.url && (
                      <a
                        className={styles.button}
                        color="inherit"
                        href={appleMeta?.url ?? ''}
                        rel="noopener noreferrer"
                        target="_blank"
                      >
                        <On3IconApplePodcast height={30} width={30} />
                        <span className={styles.buttonText}>
                          Apple Podcasts
                        </span>
                      </a>
                    )}
                    {spotifyMeta?.url && (
                      <a
                        className={styles.button}
                        href={spotifyMeta?.url ?? ''}
                        rel="noopener noreferrer"
                        target="_blank"
                      >
                        <On3IconSpotify height={30} width={30} />
                        <span className={styles.buttonText}>Spotify</span>
                      </a>
                    )}
                    {youTubeMeta?.url && (
                      <a
                        className={styles.button}
                        href={youTubeMeta?.url ?? ''}
                        rel="noopener noreferrer"
                        target="_blank"
                      >
                        <On3IconYoutube fill="#FE3B1F" height={30} width={30} />
                        <span className={styles.buttonText}>YouTube</span>
                      </a>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </section>
        {podcastSource && (
          <section>
            <h2>Latest Podcast Episodes</h2>
            <iframe src={podcastSource} title={title} />
          </section>
        )}
      </div>
    </>
  );
};
